import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { TokenIcon } from "../components/TokenIcon";
import { api, ApiError } from "../lib/api";
import type { FaucetDrip, FaucetStatus } from "../lib/api";
import { getAddress } from "../lib/auth";
import { shortHash } from "../lib/format";
import { getExplorerTxUrl, getNetworkLabel, isFaucetEnabled } from "../lib/runtimeConfig";
import { TOKENS } from "../lib/tokens";

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

/**
 * `?address=` (the trade card links here with it when an intent is short of
 * funds), else the wallet's own, so nobody copies an address out of a wallet.
 */
function initialAddress(fromQuery: string | null): string {
  if (fromQuery !== null && ADDRESS_RE.test(fromQuery)) return fromQuery;
  return getAddress() ?? "";
}

/** Tokens the faucet mints; narrowed to the status payload once it loads. */
const DEFAULT_FAUCET_SYMBOLS = ["TRYB", "EURC", "USDC", "USDT"];

type Banner =
  | { kind: "cooldown"; text: string }
  | { kind: "muted"; text: string }
  | { kind: "error"; text: string };

/** A drip's transaction: "View tx" on the explorer, or its short hash alone without one. */
function DripTx({ txHash }: { txHash: string }) {
  const href = getExplorerTxUrl(txHash);
  if (href === null) {
    return (
      <span className="explorer-link-hash" title={txHash}>
        {shortHash(txHash)}
      </span>
    );
  }
  return (
    <a className="faucet-tx-link" href={href} target="_blank" rel="noopener noreferrer">
      View tx
    </a>
  );
}

export function FaucetPage() {
  const [searchParams] = useSearchParams();
  const walletAddress = getAddress();
  const network = getNetworkLabel();
  const [status, setStatus] = useState<FaucetStatus | null>(null);
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const [address, setAddress] = useState(() => initialAddress(searchParams.get("address")));
  const [submitting, setSubmitting] = useState(false);
  const [drips, setDrips] = useState<FaucetDrip[] | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .faucetStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (
          err instanceof ApiError &&
          (err.status === 404 || err.status === 503)
        ) {
          // Backend not deployed / faucet disabled — expected, not reported.
          setStatusUnavailable(true);
        }
        // Already reported by the api layer; a status hiccup must not block minting.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const faucetSymbols =
    status?.tokens && Object.keys(status.tokens).length > 0
      ? Object.keys(status.tokens)
      : DEFAULT_FAUCET_SYMBOLS;
  const chipTokens = TOKENS.filter((t) => faucetSymbols.includes(t.symbol));

  const addressValid = ADDRESS_RE.test(address);
  // The descriptor says whether a faucet exists; /faucet/status is the live check.
  const faucetDisabled = statusUnavailable || status?.enabled === false || !isFaucetEnabled();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!addressValid || submitting) return;
    setSubmitting(true);
    setBanner(null);
    setDrips(null);
    try {
      const res = await api.requestFaucet(address);
      setDrips(res.drips ?? []);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setBanner({ kind: "cooldown", text: "Cooldown: try again later" });
      } else if (
        err instanceof ApiError &&
        (err.status === 503 || err.status === 404)
      ) {
        setBanner({
          kind: "muted",
          text: "Faucet is not enabled on this deployment",
        });
      } else if (err instanceof ApiError && err.status === 400) {
        setBanner({
          kind: "error",
          text: err.detail ?? "Request rejected — check the address",
        });
      } else {
        // Unexpected failure — already reported via api.ts → errorReporter.
        setBanner({ kind: "error", text: "Request failed — please try again" });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Faucet</h1>
      </div>

      <section className="surface-card">
        <h2 className="surface-card-title">{network === null ? "Test tokens" : `${network} test tokens`}</h2>
        <p className="surface-card-text">
          Mints the mock client tokens{network === null ? "" : ` on ${network}`} to any address — free
          test liquidity for trying out AileFX swaps.
        </p>
        <div className="token-chip-row">
          {chipTokens.map((t) => (
            <span key={t.symbol} className="token-chip">
              <TokenIcon token={t} size={20} />
              <span>{t.symbol}</span>
            </span>
          ))}
        </div>
      </section>

      {faucetDisabled ? (
        <section className="surface-card surface-card-muted">
          <p className="surface-card-text">
            The faucet is not available on this deployment yet.
          </p>
        </section>
      ) : (
        <section className="surface-card">
          <h2 className="surface-card-title">Request tokens</h2>
          <form className="faucet-form" onSubmit={handleSubmit}>
            <input
              className="address-input"
              type="text"
              placeholder="0x…"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
              }}
              spellCheck={false}
              autoComplete="off"
              aria-label="Recipient address"
            />
            <button
              type="submit"
              className="btn btn-primary btn-faucet"
              disabled={!addressValid || submitting}
            >
              {submitting ? "Requesting…" : "Request tokens"}
            </button>
          </form>

          {walletAddress !== null && address.toLowerCase() !== walletAddress && (
            <button
              type="button"
              className="link-button faucet-use-wallet"
              onClick={() => {
                setAddress(walletAddress);
              }}
            >
              Use connected wallet ({walletAddress.slice(0, 6)}…{walletAddress.slice(-4)})
            </button>
          )}

          {banner && (
            <div
              className={
                banner.kind === "cooldown"
                  ? "inline-banner inline-banner-warn"
                  : banner.kind === "muted"
                    ? "inline-banner inline-banner-muted"
                    : "inline-banner inline-banner-error"
              }
              role="status"
            >
              {banner.text}
            </div>
          )}

          {drips && (
            <ul className="faucet-results">
              {drips.length === 0 && (
                <li className="faucet-result-row faucet-result-empty">
                  No tokens were dripped.
                </li>
              )}
              {drips.map((drip) => {
                const token = TOKENS.find((t) => t.symbol === drip.token);
                return (
                  <li key={drip.token} className="faucet-result-row">
                    {token && <TokenIcon token={token} size={22} />}
                    <span className="faucet-result-symbol">{drip.token}</span>
                    {drip.txHash ? (
                      <DripTx txHash={drip.txHash} />
                    ) : (
                      <span className="chip-error">
                        {drip.error ?? "failed"}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
